/**
 * Raw fetch helpers for the impersonation API surface.
 *
 * IMPORTANT: This module uses native fetch directly (not apiFetch from
 * api-client.ts) for the same circular-dep reason as auth/api.ts:
 *   api-client.ts dynamic-imports exitImpersonationRequest from this
 *   module to handle the IMPERSONATION_NO_REFRESH sentinel branch on 401.
 *   Using apiFetch here would create an import cycle.
 *
 * The endpoints called are:
 *   POST /v1/admin/impersonate/<org_id> (start; staff bearer required)
 *   POST /v1/admin/impersonate/exit     (idempotent; impersonation bearer required)
 *
 * Both are part of Phase 1.5 Plan 04 (already shipped in fleet-api).
 *
 * Phase 3.1 Plan 03 wires these calls into the orchestration helpers in
 * start-impersonation.ts and exit-impersonation.ts and into the
 * api-client refresh-on-401 sentinel branch.
 */
import { env } from '@/lib/env';
import { authStorage } from '@/lib/auth-storage';
import {
  ImpersonateStartResponseSchema,
  ImpersonateExitResponseSchema,
  StartImpersonationError,
  type ImpersonateStartResponse,
  type ImpersonateExitResponse,
} from './schemas';

/**
 * Fleet API base URL, resolved at CALL time (2026-06-10).
 *
 * Was a module-load-time capture; converted to a function for the same
 * reason as src/features/auth/api.ts - this module can evaluate before the
 * boot-time /config.json runtime override lands, and a load-time capture
 * would pin the build-time URL on internal deployments. No config.json
 * (the DigitalOcean deployment) resolves to the identical build-time value.
 *
 * @returns The current fleet API REST base URL.
 */
function BASE_URL(): string {
  return env.FLEET_API_URL;
}

/**
 * POST /v1/admin/impersonate/<org_id>
 *
 * Calls the impersonation start endpoint with the current active access
 * token (which must be a staff bearer; the caller must guarantee this).
 *
 * On 200: parses + validates the response shape and returns it.
 * On 4xx: throws StartImpersonationError with the HTTP status and a code
 *   derived from the body's detail field (or 'http_<status>').
 * On 5xx / network: throws StartImpersonationError with code='network'.
 *
 * @param orgId - The target client org ID to impersonate
 * @returns Validated ImpersonateStartResponse
 * @throws StartImpersonationError on any non-200
 */
export async function startImpersonationRequest(
  orgId: string,
): Promise<ImpersonateStartResponse> {
  const token = authStorage.getAccess();

  let res: Response;
  try {
    res = await fetch(`${BASE_URL()}/v1/admin/impersonate/${encodeURIComponent(orgId)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch (err) {
    throw new StartImpersonationError(
      'network',
      0,
      err instanceof Error ? err.message : 'network_error',
    );
  }

  if (!res.ok) {
    let detail = `http_${res.status}`;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      // Body may not be JSON; keep the http_<status> default.
    }
    throw new StartImpersonationError(detail, res.status);
  }

  const body = await res.json();
  return ImpersonateStartResponseSchema.parse(body);
}

/**
 * POST /v1/admin/impersonate/exit
 *
 * Calls the exit endpoint with the CURRENT active access token. During
 * normal exit-impersonation flow this is the impersonation bearer; during
 * api-client's IMPERSONATION_NO_REFRESH sentinel branch it is the same
 * (just-expired) impersonation bearer (the server treats both cases as
 * idempotent close per ADR-005 section 3 step 6).
 *
 * The function is best-effort: callers (orchestrators + api-client) treat
 * non-200 responses as "server may have already closed; proceed with the
 * client-side swap-back regardless." For this reason the function does
 * NOT throw on non-200; it returns null instead.
 *
 * @returns The validated ImpersonateExitResponse on 200, or null on any
 *   non-200 (including network failure).
 */
export async function exitImpersonationRequest(): Promise<ImpersonateExitResponse | null> {
  const token = authStorage.getAccess();

  let res: Response;
  try {
    res = await fetch(`${BASE_URL()}/v1/admin/impersonate/exit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    return null;
  }

  if (!res.ok) {
    return null;
  }

  try {
    const body = await res.json();
    return ImpersonateExitResponseSchema.parse(body);
  } catch {
    return null;
  }
}
